/**
 * PII scrubber for Sentry events — the last thing that touches an event
 * before it leaves the device.
 *
 * `stripPII` was written in response to a real leak and then shipped with
 * zero consumers (audit Phase 2, §7). This module is where it belongs:
 * both SDKs (`sentry.ts` native, `sentry.web.ts` web) run every outgoing
 * event through `scrubSentryEventPII` in their `beforeSend` hook.
 *
 * This is defence in depth, not the only defence. `sendDefaultPii` is
 * false on both SDKs, `setSentryUser` attaches nothing but a profile id,
 * and Session Replay masks all text. The scrubber exists because none of
 * those stop an email or a phone number that a developer interpolated
 * into an error message or a breadcrumb.
 *
 * Two complementary passes:
 *  - Free text (`message`, exception values, breadcrumb messages, URLs)
 *    goes through `stripPII`'s regexes.
 *  - Structured data (breadcrumb data, extras, contexts, request payload)
 *    is additionally walked key by key: a value under a key like `phone`
 *    or `token` is dropped whole, because `76661234` on its own carries
 *    no cue for a regex to latch onto.
 *
 * The event is mutated in place and the caller returns its own reference,
 * so the SDK's `ErrorEvent` type flows through `beforeSend` untouched.
 */

import { PII_SENSITIVE_KEY } from './pii-patterns'
import { stripPII } from './sanitize'

const REDACTED = '[REDACTED]'
const RAW_PAYLOAD_KEY =
  /^(?:body|headers?|cookies?|query_string|payload|raw[_-]?(?:body|response|request))$/i

/** Deepest nesting level walked inside structured payloads. */
const MAX_DEPTH = 5

/** Keys whose string values are locations rather than human-readable text. */
const URL_LIKE_KEY = /url|uri|href|origin|referr?er/i

/** A URL embedded in an exception or breadcrumb can carry a token in its query. */
const URL_IN_TEXT = /\bhttps?:\/\/[^\s<>"']+/gi

/** Safe, developer-owned fingerprint labels (never opaque provider text). */
const SAFE_FINGERPRINT_SEGMENT = /^[a-z][a-z0-9_.:-]{0,79}$/i

/**
 * Structural subset of Sentry's `Event`. Declared locally instead of
 * imported so one scrubber can serve both SDKs: `@sentry/react-native`
 * and `@sentry/react` pin different `@sentry/core` versions, and their
 * `ErrorEvent` types are identical in shape but not in identity.
 */
export interface ScrubbableSentryEvent {
  message?: string
  logentry?: { message?: string; params?: unknown[] }
  exception?: {
    values?: {
      value?: string
      stacktrace?: {
        frames?: {
          filename?: string
          abs_path?: string
          function?: string
          module?: string
          vars?: Record<string, unknown>
          pre_context?: string[]
          post_context?: string[]
          context_line?: string
        }[]
      }
    }[]
  }
  user?: { id?: string | number; [key: string]: unknown }
  debug_meta?: { images?: { code_file?: string; debug_id?: string }[] }
  breadcrumbs?: { message?: string; data?: Record<string, unknown> }[]
  contexts?: Record<string, unknown>
  extra?: Record<string, unknown>
  tags?: Record<string, unknown>
  fingerprint?: string[]
  transaction?: string
  spans?: { description?: string; data?: Record<string, unknown> }[]
  request?: {
    url?: string
    data?: unknown
    query_string?: unknown
    cookies?: Record<string, string>
    headers?: Record<string, string>
  }
}

/**
 * Preserve only a URL's path. Query strings and fragments regularly contain
 * credentials, state and account IDs, while the path is sufficient to locate
 * the failed endpoint. This deliberately uses string parsing so it works in
 * the native runtime too, before any URL polyfill is installed.
 */
function scrubUrl(value: string): string {
  const withoutQueryOrFragment = value.split(/[?#]/, 1)[0] ?? ''
  const absoluteMatch = withoutQueryOrFragment.match(/^[a-z][a-z0-9+.-]*:\/\/[^/]+(\/.*)?$/i)
  return scrubText(absoluteMatch ? (absoluteMatch[1] ?? '/') : withoutQueryOrFragment)
}

/** Bundle/image locations must keep the same clean public URL for Debug ID matching. */
function scrubBundleLocation(value: string): string {
  try {
    const url = new URL(value)
    if (url.protocol === 'http:' || url.protocol === 'https:') {
      return `${url.protocol}//${url.host}${scrubText(url.pathname)}`
    }
  } catch {
    // Native relative/app:/// bundle names need no URL parser to remain useful.
  }
  return scrubText(value.split(/[?#]/, 1)[0] ?? '')
}

/** Scrub free text and replace any embedded URL with its non-sensitive path. */
function scrubText(value: string): string {
  if (value.length > 4096) return '[REDACTED OVERSIZED TEXT]'
  return stripPII(
    value
      .replace(/\{[\s\S]*|\[[\s\S]*"[\s\S]*/g, '[REDACTED PAYLOAD]')
      // Provider messages can already be clipped by the capture normalizer.
      // Redact from the opening tag, without requiring a closing XML/HTML tag.
      .replace(/<(?:\?xml|[A-Za-z][\w:-]*\b)[\s\S]*/gi, '[REDACTED PAYLOAD]')
      .replace(URL_IN_TEXT, (url) => scrubUrlWithoutRecursion(url))
      .replace(/\bBearer\s+\S+/gi, 'Bearer [REDACTED]')
      .replace(/\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+(?:\.[A-Za-z0-9_-]+)?/g, REDACTED)
      .replace(/\b(?:password|secret|token|api[_-]?key|authorization)\s*[:=]\s*\S+/gi, REDACTED)
      .replace(/\b(?:\d[ -]?){13,19}\b/g, '[REDACTED NUMBER]'),
  )
}

/** `scrubText` uses this narrow helper to avoid recursively scanning a URL. */
function scrubUrlWithoutRecursion(value: string): string {
  const withoutQueryOrFragment = value.split(/[?#]/, 1)[0] ?? ''
  const absoluteMatch = withoutQueryOrFragment.match(/^[a-z][a-z0-9+.-]*:\/\/[^/]+(\/.*)?$/i)
  return stripPII(absoluteMatch ? (absoluteMatch[1] ?? '/') : withoutQueryOrFragment)
}

/** Recursively redact a structured value: sensitive keys out, strings scrubbed. */
function scrubStructured(value: unknown, depth: number): unknown {
  if (typeof value === 'string') return scrubText(value)
  if (value === null || typeof value !== 'object') return value
  // Never return the original nested object at the bound: that reintroduced
  // the unsanitized subtree (and cycles) into the outgoing envelope.
  if (depth >= MAX_DEPTH) return REDACTED

  if (Array.isArray(value)) {
    return value.slice(0, 100).map((item) => scrubStructured(item, depth + 1))
  }

  const scrubbed: Record<string, unknown> = {}
  for (const [key, item] of Object.entries(value as Record<string, unknown>)) {
    scrubbed[scrubText(key)] =
      PII_SENSITIVE_KEY.test(key) || RAW_PAYLOAD_KEY.test(key)
        ? REDACTED
        : typeof item === 'string' && URL_LIKE_KEY.test(key)
          ? scrubUrl(item)
          : scrubStructured(item, depth + 1)
  }
  return scrubbed
}

/** Scrub a record in place so the caller keeps the SDK's own object identity. */
function scrubRecordInPlace(record: Record<string, unknown>): void {
  for (const [key, value] of Object.entries(record)) {
    const safeKey = scrubText(key)
    if (safeKey !== key) delete record[key]
    record[safeKey] =
      PII_SENSITIVE_KEY.test(key) || RAW_PAYLOAD_KEY.test(key)
        ? REDACTED
        : typeof value === 'string' && URL_LIKE_KEY.test(key)
          ? scrubUrl(value)
          : scrubStructured(value, 1)
  }
}

/** Replace every value in a record. Used for cookies — all of them are credentials. */
function redactAllValuesInPlace(record: Record<string, string>): void {
  for (const key of Object.keys(record)) {
    record[key] = REDACTED
  }
}

/** Tags are indexed and retained longer, so values need the same PII policy as extras. */
function scrubTagsInPlace(tags: Record<string, unknown>): void {
  for (const [key, value] of Object.entries(tags)) {
    const safeKey = scrubText(key)
    if (safeKey !== key) delete tags[key]
    tags[safeKey] = PII_SENSITIVE_KEY.test(key) ? REDACTED : scrubStructured(value, 1)
  }
}

/** Do not let arbitrary provider text become an indefinitely retained grouping key. */
function scrubFingerprintSegment(value: string): string {
  const scrubbed = scrubText(value)
  return SAFE_FINGERPRINT_SEGMENT.test(scrubbed) ? scrubbed : REDACTED
}

/**
 * Strip emails, phone numbers and credential-shaped values out of an
 * outgoing Sentry event. Mutates `event`; returns nothing so callers keep
 * returning their own typed reference from `beforeSend`.
 */
export function scrubSentryEventPII(event: ScrubbableSentryEvent): void {
  if (typeof event.message === 'string') {
    event.message = scrubText(event.message)
  }

  if (typeof event.logentry?.message === 'string') {
    event.logentry.message = scrubText(event.logentry.message)
  }
  if (event.logentry?.params) {
    event.logentry.params = event.logentry.params
      .slice(0, 100)
      .map((param) => scrubStructured(param, 1))
  }

  for (const exception of event.exception?.values ?? []) {
    if (typeof exception.value === 'string') {
      exception.value = scrubText(exception.value)
    }
    for (const frame of exception.stacktrace?.frames ?? []) {
      if (typeof frame.filename === 'string') frame.filename = scrubBundleLocation(frame.filename)
      if (typeof frame.abs_path === 'string') frame.abs_path = scrubBundleLocation(frame.abs_path)
      if (typeof frame.function === 'string') frame.function = scrubText(frame.function)
      if (typeof frame.module === 'string') frame.module = scrubText(frame.module)
      if (frame.vars) frame.vars = {}
      if (frame.pre_context) frame.pre_context = []
      if (frame.post_context) frame.post_context = []
      if (frame.context_line) frame.context_line = REDACTED
    }
  }
  if (event.user) {
    const id = event.user.id
    event.user = typeof id === 'string' || typeof id === 'number' ? { id } : {}
  }
  for (const image of event.debug_meta?.images ?? []) {
    if (typeof image.code_file === 'string') image.code_file = scrubBundleLocation(image.code_file)
  }

  for (const breadcrumb of event.breadcrumbs ?? []) {
    if (typeof breadcrumb.message === 'string') {
      breadcrumb.message = scrubText(breadcrumb.message)
    }
    if (breadcrumb.data) scrubRecordInPlace(breadcrumb.data)
  }

  if (event.extra) scrubRecordInPlace(event.extra)
  if (event.tags) scrubTagsInPlace(event.tags)
  if (event.fingerprint) {
    event.fingerprint = event.fingerprint.map(scrubFingerprintSegment)
  }

  if (typeof event.transaction === 'string') {
    event.transaction = scrubUrl(event.transaction)
  }

  for (const span of event.spans ?? []) {
    if (typeof span.description === 'string') span.description = scrubUrl(span.description)
    if (span.data) scrubRecordInPlace(span.data)
  }

  if (event.contexts) {
    for (const [name, context] of Object.entries(event.contexts)) {
      // Trace/device/app scalars still survive the key-based scrub. SDK-owned
      // contexts can contain custom nested data and must not bypass it.
      const safeName = scrubText(name)
      if (safeName !== name) delete event.contexts[name]
      event.contexts[safeName] = scrubStructured(context, 1)
    }
  }

  const request = event.request
  if (request) {
    if (typeof request.url === 'string') request.url = scrubUrl(request.url)
    // Request bodies, query strings and headers can contain OAuth state,
    // bank payloads or credentials under arbitrary keys. They have no
    // diagnostic value once the safe endpoint/status tags are retained.
    if (request.query_string !== undefined) request.query_string = REDACTED
    if (request.data !== undefined) request.data = REDACTED
    if (request.headers) redactAllValuesInPlace(request.headers)
    // Cookie values are session credentials without exception, and none of
    // them ever helped debug a crash — drop the lot, keep the names.
    if (request.cookies) redactAllValuesInPlace(request.cookies)
  }
}
